import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PencilIcon, PlusIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { City, EventCityMap, EventProjectMap, Project } from '@/lib/types';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DELETE_CONSEQUENCE, mapKeyOf } from './event-map-rules';
import { EventMapFormModal, type EventMapFormTarget } from './EventMapFormModal';
import { subtitleOf, useEventMapTargets } from './use-event-map-targets';

/*
 * One mapping table — festivals or cities.
 *
 * ⚠️ **EACH PANEL OWNS ITS OWN READ, AND THAT IS WHY THE PAGE MOUNTS TWO RATHER
 * THAN FETCHING BOTH ITSELF.** Nineteen city mappings failing to load must not
 * take the seven festival mappings off the screen: the two tables answer
 * different questions, and a reader who came to check the festivals can still do
 * that while the other is broken. A single read for both would make every
 * failure a whole-page failure.
 *
 * ⚠️ **NO PAGER AND NO FILTERS, DELIBERATELY.** The list is read in full —
 * `listAllEventProjectMaps` throws rather than resolving short — because the
 * question this screen answers is *"is every key mapped, and mapped to the right
 * thing"*, which page 1 of N cannot answer: a missing mapping and a mapping on
 * the next page look identical. ⛔ **And a `isActive` filter must never default
 * to hiding the retired rows**: a key the form has stopped accepting is exactly
 * what somebody is hunting, and a filter nobody set would remove it silently in
 * the direction of a shorter list that reads as a tidy configuration.
 */

export interface EventMapPanelProps {
  kind: EventMapFormTarget['kind'];
  /** The heading's own id, so the page owns the document's heading structure. */
  headingId: string;
}

const SKELETON_ROWS = 3;

/**
 * The table's floor, in px.
 *
 * 🔴 **AN INLINE STYLE, NEVER A CLASS.** Tailwind v4 scans SOURCE TEXT, so a
 * computed `min-w-[…]` emits no rule at all — the class lands in the DOM and does
 * nothing, which looks exactly like a table that chose to have no floor.
 *
 * ⚠️ **Each term is the pixel value of the header class beside it**, written out
 * together so the arithmetic cannot drift from the rendering. `w-28` is
 * `28 × 0.25rem` = 112px. Key and "Maps to" are the fluid columns and carry no
 * `w-*`, so they contribute floors rather than widths.
 */
const KEY_COLUMN_FLOOR = 190;
const TARGET_COLUMN_FLOOR = 260;
const TABLE_MIN_WIDTH =
  KEY_COLUMN_FLOOR + // Key      (fluid)
  TARGET_COLUMN_FLOOR + // Maps to  (fluid)
  112 + // Status   w-28
  112; // Actions  w-28

const TABLE_STYLE = { minWidth: `${TABLE_MIN_WIDTH}px` };

const COPY = {
  project: {
    title: 'Festival keys',
    keyHeading: 'Festival key',
    targetHeading: 'SEED EMERGE project',
    createLabel: 'Map a festival key',
    noun: 'festival mapping',
    nounPlural: 'festival mappings',
    emptyTitle: 'No festival keys are mapped',
    emptyBody:
      'The 2027 form refuses every submission until each festival key it sends resolves to a project here.',
    loadFailed: 'Couldn’t load the festival mappings.',
  },
  city: {
    title: 'City keys',
    keyHeading: 'City key',
    targetHeading: 'SEED EMERGE city',
    createLabel: 'Map a city key',
    noun: 'city mapping',
    nounPlural: 'city mappings',
    emptyTitle: 'No city keys are mapped',
    emptyBody:
      'The 2027 form refuses a submission naming any city that does not resolve to a city here.',
    loadFailed: 'Couldn’t load the city mappings.',
  },
} as const;

function MapIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M9 4.5 4 6.5v13l5-2 6 2 5-2v-13l-5 2Z" />
      <path d="M9 4.5v13" />
      <path d="M15 6.5v13" />
    </svg>
  );
}

type Row = EventProjectMap | EventCityMap;

/** The id of whatever a row points at, read once per arm rather than at three sites. */
function targetIdOf(row: Row): number {
  return 'project' in row ? row.project.id : row.city.id;
}

/** The NAME the wire gave for it — always available, even when the master read failed. */
function targetNameOf(row: Row): string {
  return 'project' in row ? row.project.name : row.city.name;
}

export function EventMapPanel({ kind, headingId }: EventMapPanelProps) {
  const copy = COPY[kind];

  const [rows, setRows] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** A 403 is a role change under a live token, not a validation failure. */
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [formTarget, setFormTarget] = useState<EventMapFormTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * The records behind the `{id, name}` on each row. ⚠️ **Not a nicety: the
   * mapping row carries no season**, and `Project.name` is not unique, so
   * without this the table prints two festivals under one word and cannot be
   * used to check a mapping at all.
   */
  const targets = useEventMapTargets(kind);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const read =
        kind === 'project' ? api.listAllEventProjectMaps(signal) : api.listAllEventCityMaps(signal);
      return read
        .then((data) => {
          setRows(data);
          setError(null);
          setForbidden(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          if (caught instanceof ApiError && caught.status === 403) {
            /*
             * The token is still valid and the role behind it no longer reaches
             * these tables. No `details` on a 403, so there is nothing to hang
             * on a field — the whole surface goes with a banner saying why.
             */
            setForbidden(
              caught.message || 'Your account no longer has permission to see these mappings.',
            );
            setError(null);
            setRows(null);
            return;
          }
          setForbidden(null);
          setError(caught instanceof Error ? caught.message : copy.loadFailed);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [kind, copy.loadFailed],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const initialLoad = loading && rows === null;
  const list = useMemo(() => rows ?? [], [rows]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (kind === 'project') await api.deleteEventProjectMap(pendingDelete.id);
      else await api.deleteEventCityMap(pendingDelete.id);
      setPendingDelete(null);
      /*
       * Re-read rather than dropping the row locally. The list is read whole,
       * so a re-read is one request and it is the only thing that can confirm
       * the row is actually gone.
       */
      await load();
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Verbatim — a row already gone, or a refusal, comes back naming what
        // happened, and the server's sentence beats anything invented here.
        setDeleteError(caught.message);
      } else {
        setDeleteError('Could not reach the server. The mapping has NOT been removed.');
      }
    } finally {
      setDeleting(false);
    }
  }

  /*
   * ⛔ A FAILED READ SAYS NOTHING. IT MUST NEVER SAY "0 festival mappings".
   *
   * `rows === null` means WE COULD NOT ASK; `rows === []` means we asked and
   * there are none. `list` is `rows ?? []`, so counting `list` collapses those
   * two into one sentence — and on THIS screen that sentence is a claim about
   * the public 2027 interest form: an unmapped key is a 422 at submission time,
   * as `copy.emptyBody` says. "0 festival mappings" over a read that never came
   * back announces that the form is refusing every submission. Nobody knows.
   *
   * ⚠️ IT IS ALSO ANNOUNCED. `countLabel` feeds a `role="status"` region as well
   * as the visible description, so a screen reader hears the count and then,
   * separately, that the list could not be read.
   *
   * ⚠️ The empty state below already handles this correctly, which is what makes
   * this a GAP rather than a decision — it switches to "Nothing to show" and
   * withholds its create action on a failed read. The count did not get the same
   * treatment, and the two sit in one panel.
   *
   * 🔴 THE HONEST ZERO SURVIVES, AND THAT IS THE POINT RATHER THAN A DETAIL. An
   * empty table is the ORDINARY day-one state here — nothing seeds these tables
   * — so `rows === []` still takes the template-string branch and still reads
   * "0 festival mappings". Silencing every zero to be rid of one false zero
   * trades a wrong statement for a missing one.
   *
   * `null` rather than a dash or "Unknown": the failure already has a sentence
   * of its own directly beneath, and a second phrasing of the same fact invites
   * a reader to take them for two different facts.
   */
  const countLabel =
    rows === null
      ? loading
        ? 'Loading…'
        : null
      : `${list.length} ${list.length === 1 ? copy.noun : copy.nounPlural}`;

  function renderActions(row: Row): ReactNode {
    const label = mapKeyOf(row);
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<PencilIcon />}
          aria-label={`Edit the mapping for ${label}`}
          title={`Edit the mapping for ${label}`}
          onClick={() =>
            setFormTarget(
              /*
               * ⚠️ **Narrowed on the ROW, not on `kind`.** The union is what
               * stops a city row being handed to the project arm, and reading
               * `kind` here would hand that guarantee back to a variable the
               * compiler cannot relate to the row.
               */
              'project' in row ? { kind: 'project', row } : { kind: 'city', row },
            )
          }
        />
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            setDeleteError(null);
            setPendingDelete(row);
          }}
        >
          Delete
        </Button>
      </div>
    );
  }

  return (
    <Panel className="min-w-0" aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title={copy.title}
        description={countLabel}
        actions={
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<PlusIcon />}
            onClick={() =>
              setFormTarget(kind === 'project' ? { kind: 'project' } : { kind: 'city' })
            }
          >
            {copy.createLabel}
          </Button>
        }
      />

      <p role="status" aria-live="polite" className="sr-only">
        {countLabel}
      </p>

      {forbidden && (
        <Notice
          tone="danger"
          role="alert"
          className="m-4"
          title="Your account can no longer see these mappings."
        >
          <p className="m-0 min-w-0 [overflow-wrap:anywhere]">
            {forbidden} Sign out and back in to pick up the change.
          </p>
        </Notice>
      )}

      {error && (
        <Notice tone="danger" role="alert" className="m-4" title={copy.loadFailed}>
          <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      {/*
        * ⚠️ **A SEPARATE, QUIETER NOTICE FOR THE MASTER READ, AND IT IS NOT AN
        * ERROR.** The mappings themselves loaded; what failed is the list this
        * screen joins to for a project's season. The rows below are still
        * correct and still worth reading — they simply cannot be checked against
        * a season — so wording this as a failure would send somebody hunting a
        * fault in data that is fine.
        */}
      {!forbidden && targets.error && (
        <Notice tone="neutral" className="mx-4 my-2">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            Each mapping&rsquo;s name is shown, but the cycle and dates behind it could not be
            read, so two seasons cannot be told apart here. {targets.error}
          </span>
        </Notice>
      )}

      {!forbidden && (
        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && rows !== null && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table style={TABLE_STYLE}>
                <MapTableHead copy={copy} />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <TR key={i}>
                      <TD>
                        <Skeleton className="h-3 w-32" />
                      </TD>
                      <TD>
                        <div className="grid gap-1.5">
                          <Skeleton className="h-3 w-48" />
                          <Skeleton className="h-2.5 w-32" />
                        </div>
                      </TD>
                      <TD>
                        <Skeleton className="h-4 w-16" />
                      </TD>
                      <TD numeric>
                        <div className="flex justify-end gap-1.5">
                          <Skeleton className="h-6 w-6" />
                          <Skeleton className="h-6 w-14" />
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : list.length === 0 ? (
            <EmptyState
              icon={<MapIcon />}
              title={error ? 'Nothing to show' : copy.emptyTitle}
              /*
               * ⚠️ **The empty state names a CONSEQUENCE rather than implying
               * rows are on their way.** Nothing seeds these tables: a mapping
               * exists because an administrator entered it, and until one does
               * the public form refuses every submission naming that key.
               */
              description={error ? 'Couldn’t load the list — see the message above.' : copy.emptyBody}
              action={
                error ? undefined : (
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<PlusIcon />}
                    onClick={() =>
                      setFormTarget(kind === 'project' ? { kind: 'project' } : { kind: 'city' })
                    }
                  >
                    {copy.createLabel}
                  </Button>
                )
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table style={TABLE_STYLE}>
                <MapTableHead copy={copy} />
                <TBody>
                  {list.map((row) => (
                    <TR key={row.id} interactive>
                      <TD>
                        {/*
                          * ⚠️ **A `<code>`, and rendered EXACTLY as stored.**
                          * These are admin-app's strings — underscores on a
                          * festival key — and this app never normalises,
                          * case-folds or rewrites one. A proportional font
                          * hides the difference between an underscore and a
                          * hyphen, which is the whole of what makes a key
                          * wrong.
                          */}
                        <code className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                          {mapKeyOf(row)}
                        </code>
                      </TD>
                      <TD>
                        <TargetCell
                          row={row}
                          record={targets.byId.get(targetIdOf(row)) ?? null}
                          loading={targets.loading}
                          lookupFailed={targets.error !== null}
                        />
                      </TD>
                      <TD>
                        {/*
                          * ⚠️ **`Accepting` / `Not accepting`, never
                          * `Active` / `Inactive`.** The flag governs what the
                          * PUBLIC FORM takes, not whether this row is shown —
                          * and "Inactive" reads as a row that has been tidied
                          * away rather than as a door that is closed.
                          */}
                        {row.isActive ? (
                          <Badge tone="success" dot>
                            Accepting
                          </Badge>
                        ) : (
                          <Badge tone="neutral" dot>
                            Not accepting
                          </Badge>
                        )}
                      </TD>
                      <TD numeric>{renderActions(row)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>
      )}

      <EventMapFormModal
        open={formTarget !== null}
        onClose={() => setFormTarget(null)}
        /* `formTarget` is null only while the modal is closed, and the modal
           returns null then — so this fallback is never rendered from. */
        target={formTarget ?? (kind === 'project' ? { kind: 'project' } : { kind: 'city' })}
        onSaved={() => void load()}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        tone="danger"
        title={pendingDelete ? `Remove the mapping for ${mapKeyOf(pendingDelete)}?` : 'Remove mapping?'}
        description={
          <>
            {/*
              * ⛔ **PROTECTED — a helper-text sweep must not take this.** It
              * names what is destroyed, and it names the REVERSIBLE alternative,
              * because both actions stop new submissions and without that
              * sentence the irreversible one reads as the tidier way to do the
              * same job. See `DELETE_CONSEQUENCE`.
              */}
            <p className="m-0">{DELETE_CONSEQUENCE}</p>
            {pendingDelete && (
              <p className="mt-2 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                <code>{mapKeyOf(pendingDelete)}</code> currently means{' '}
                {targetNameOf(pendingDelete)}.
              </p>
            )}
          </>
        }
        confirmLabel="Remove mapping"
        loading={deleting}
        error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
      />
    </Panel>
  );
}

function MapTableHead({ copy }: { copy: (typeof COPY)[keyof typeof COPY] }) {
  return (
    <THead>
      <TR>
        <TH>{copy.keyHeading}</TH>
        <TH>{copy.targetHeading}</TH>
        <TH className="w-28">Status</TH>
        <TH className="w-28" numeric>
          Actions
        </TH>
      </TR>
    </THead>
  );
}

/**
 * What a key resolves to, with enough beside it to check.
 *
 * 🔴 **THE NAME COMES FROM THE ROW AND THE SEASON COMES FROM THE MASTER, AND THE
 * TWO FAIL SEPARATELY.** The wire always carries the name, so it always renders;
 * the season needs the project list, which may still be arriving or may have
 * failed. Folding the two would mean a failed master read blanking a name the
 * server did give us.
 *
 * ⚠️ **A record MISSING from a loaded master is a real and alarming state** —
 * the project has been deleted out from under a live mapping — and it is worded
 * as one, not left as a blank that reads like a season nobody filled in. It is
 * only claimed once the master has actually loaded: `lookupFailed` and `loading`
 * both suppress it, because a read that did not happen is not evidence about a
 * record.
 */
function TargetCell({
  row,
  record,
  loading,
  lookupFailed,
}: {
  row: Row;
  record: Project | City | null;
  loading: boolean;
  lookupFailed: boolean;
}) {
  const name = targetNameOf(row);

  return (
    <div className="min-w-0">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{name}</p>
      <TargetSubtitle record={record} loading={loading} lookupFailed={lookupFailed} />
    </div>
  );
}

function TargetSubtitle({
  record,
  loading,
  lookupFailed,
}: {
  record: Project | City | null;
  loading: boolean;
  lookupFailed: boolean;
}) {
  if (record === null) {
    /* Still arriving, or the master read failed — neither is a claim about this
       record, and the panel's own notice already explains the second. */
    if (loading || lookupFailed) {
      return <p className="mt-0.5 mb-0 h-4 text-xs text-fg-subtle" aria-hidden="true" />;
    }
    return (
      <p className="mt-0.5 mb-0 text-xs text-danger [overflow-wrap:anywhere]">
        No longer in the catalogue — this key resolves to a record that has been removed.
      </p>
    );
  }

  const subtitle = subtitleOf(record);
  return (
    <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
      {subtitle || <span className="text-fg-subtle">Nothing recorded to tell it apart</span>}
      {!record.isActive && <span className="text-warning"> · retired</span>}
    </p>
  );
}
